import { APP_TIMEZONE } from "@/config/site";

/**
 * Timezone helpers — mirror of the backend `src/helpers/timezone.ts`.
 *
 * Day windows store zone-less wall-clock times (`YYYY-MM-DD` + `HH:MM`) while
 * roles and event dates are absolute instants. The API reconciles the two in
 * `APP_TIMEZONE`, never in the server's zone; these helpers do the same in the
 * browser, never in the viewer's zone, so what the UI validates and lists is
 * exactly what the API accepts and deletes.
 */

interface ZonedParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

const MIDNIGHT_AS_24 = 24;

const formatters = new Map<string, Intl.DateTimeFormat>();

const getFormatter = (timeZone: string) => {
  const cached = formatters.get(timeZone);

  if (cached) {
    return cached;
  }

  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  formatters.set(timeZone, formatter);
  return formatter;
};

/** Wall-clock calendar/time parts of `instant` as seen in `timeZone`. */
const getZonedParts = (instant: Date, timeZone: string): ZonedParts => {
  const lookup: Partial<Record<Intl.DateTimeFormatPartTypes, number>> = {};

  for (const part of getFormatter(timeZone).formatToParts(instant)) {
    if (part.type !== "literal") {
      lookup[part.type] = Number(part.value);
    }
  }

  const hour = lookup.hour ?? 0;

  return {
    year: lookup.year ?? 0,
    month: lookup.month ?? 1,
    day: lookup.day ?? 1,
    // Some ICU builds emit "24" for midnight under the h23 cycle.
    hour: hour === MIDNIGHT_AS_24 ? 0 : hour,
    minute: lookup.minute ?? 0,
    second: lookup.second ?? 0,
  };
};

/** Offset (ms) of `timeZone` at `instant`, positive when ahead of UTC. */
const getTimeZoneOffsetMs = (timeZone: string, instant: Date) => {
  const parts = getZonedParts(instant, timeZone);
  const wallClockAsUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
  );

  return wallClockAsUtc - Math.floor(instant.getTime() / 1000) * 1000;
};

const padUnit = (value: number) => String(value).padStart(2, "0");

/** Calendar day (`YYYY-MM-DD`) of an absolute instant in `timeZone`. */
export const formatZonedDayKey = (
  value: Date | string,
  timeZone: string = APP_TIMEZONE,
) => {
  const parts = getZonedParts(new Date(value), timeZone);

  return `${parts.year}-${padUnit(parts.month)}-${padUnit(parts.day)}`;
};

/**
 * Wall clock (`HH:MM`, 24h, ASCII digits) of an absolute instant in
 * `timeZone` — the shape day windows and `<input type="time">` use. Unlike
 * {@link formatZonedTime} it never depends on a locale, so it is safe to parse
 * or to feed back into a window.
 */
export const formatZonedWallClock = (
  value: Date | string,
  timeZone: string = APP_TIMEZONE,
) => {
  const parts = getZonedParts(new Date(value), timeZone);

  return `${padUnit(parts.hour)}:${padUnit(parts.minute)}`;
};

const displayFormatters = new Map<string, Intl.DateTimeFormat>();

/**
 * Cached `Intl.DateTimeFormat` pinned to `timeZone` (default `APP_TIMEZONE`).
 * Building a formatter resolves locale data and is far costlier than using
 * one, so grids and lists formatting hundreds of instants share one instance
 * per (locale, options, zone).
 */
export const getZonedFormatter = (
  locale: string | undefined,
  options: Intl.DateTimeFormatOptions,
  timeZone: string = APP_TIMEZONE,
) => {
  const key = `${locale ?? ""}|${timeZone}|${JSON.stringify(options)}`;
  const cached = displayFormatters.get(key);

  if (cached) {
    return cached;
  }

  const formatter = new Intl.DateTimeFormat(locale, { ...options, timeZone });
  displayFormatters.set(key, formatter);
  return formatter;
};

const ZONED_TIME_OPTIONS: Intl.DateTimeFormatOptions = {
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
};

/**
 * Wall-clock time (`HH:MM`, 24h) of an absolute instant in `APP_TIMEZONE` —
 * the zone day windows are written in, so a poste reads "22:00" next to the
 * "22:00 → 06:00" window it sits in, whatever the viewer's own zone.
 */
export const formatZonedTime = (
  value: Date | string,
  locale?: string,
  timeZone: string = APP_TIMEZONE,
) =>
  getZonedFormatter(locale, ZONED_TIME_OPTIONS, timeZone).format(
    new Date(value),
  );

/** How far around a wall clock to look for the offsets that may apply to it. */
const OFFSET_PROBE_MS = 30 * 60 * 60 * 1000;

/**
 * Resolved wall clocks, as epoch ms. Validating a 30-day event resolves the
 * same few dozen (date, time) pairs on every keystroke, each costing up to six
 * `formatToParts` calls; the answer never changes, so it is computed once.
 * Bounded so a long session cannot grow it without limit.
 */
const resolvedWallClocks = new Map<string, number>();
const MAX_RESOLVED_WALL_CLOCKS = 5000;

const resolveWallClock = (
  dateKey: string,
  timeValue: string,
  dayOffset: number,
  timeZone: string,
) => {
  const [year, month, day] = dateKey.split("-").map(Number);
  const [hours, minutes] = timeValue.split(":").map(Number);
  const naiveUtc = Date.UTC(
    year,
    month - 1,
    day + dayOffset,
    Number.isFinite(hours) ? hours : 0,
    Number.isFinite(minutes) ? minutes : 0,
  );
  // Offsets in force around the date: one before and one after any DST
  // change close to it (changes are months apart).
  const candidates = [
    ...new Set(
      [-OFFSET_PROBE_MS, 0, OFFSET_PROBE_MS].map((shift) =>
        getTimeZoneOffsetMs(timeZone, new Date(naiveUtc + shift)),
      ),
    ),
  ];
  // An offset is valid when the instant it yields really shows that wall
  // clock: two valid offsets = ambiguous, none = the wall clock is skipped.
  const valid = candidates.filter(
    (offset) =>
      getTimeZoneOffsetMs(timeZone, new Date(naiveUtc - offset)) === offset,
  );
  const offset = Math.min(...(valid.length > 0 ? valid : candidates));

  return naiveUtc - offset;
};

/**
 * Absolute instant of a wall-clock time in `timeZone`: `dateKey`
 * (`YYYY-MM-DD`) at `timeValue` (`HH:MM`), `dayOffset` days later.
 *
 * Around a DST change it resolves exactly like the backend (and PostgreSQL's
 * `timestamp AT TIME ZONE`): an ambiguous wall clock (fall-back hour, lived
 * twice) takes standard time, the smaller offset; a wall clock that does not
 * exist (spring-forward hour) is read with the pre-transition offset, which
 * pushes it forward.
 */
export const zonedWallClockToInstant = (
  dateKey: string,
  timeValue: string,
  dayOffset = 0,
  timeZone: string = APP_TIMEZONE,
) => {
  const cacheKey = `${timeZone}|${dateKey}|${timeValue}|${dayOffset}`;
  const cached = resolvedWallClocks.get(cacheKey);

  if (cached !== undefined) {
    return new Date(cached);
  }

  const resolved = resolveWallClock(dateKey, timeValue, dayOffset, timeZone);

  if (resolvedWallClocks.size >= MAX_RESOLVED_WALL_CLOCKS) {
    resolvedWallClocks.clear();
  }

  resolvedWallClocks.set(cacheKey, resolved);
  return new Date(resolved);
};

const MIDNIGHT = "00:00";

/**
 * Midnight of the calendar day `dateKey` in `timeZone` — the instant the API
 * stores for an event date. Sending this, instead of a browser-local Date,
 * makes the saved day independent of the viewer's zone: read back through
 * {@link formatZonedDayKey} it is `dateKey` again.
 */
export const zonedDayKeyToInstant = (
  dateKey: string,
  timeZone: string = APP_TIMEZONE,
) => zonedWallClockToInstant(dateKey, MIDNIGHT, 0, timeZone);

const MS_PER_MINUTE = 60 * 1000;

/**
 * Move `value` by `days` calendar days in `timeZone`, keeping its wall-clock
 * time (22:00 stays 22:00 across a DST change) — mirror of the backend
 * `shiftZonedInstantByDays`, so the postes a phase shift moves land exactly
 * where the API puts them. Seconds and milliseconds are carried over.
 */
export const shiftZonedInstantByDays = (
  value: Date | string,
  days: number,
  timeZone: string = APP_TIMEZONE,
) => {
  const instant = new Date(value);

  if (days === 0) {
    return instant;
  }

  const parts = getZonedParts(instant, timeZone);
  const subMinuteMs =
    ((instant.getTime() % MS_PER_MINUTE) + MS_PER_MINUTE) % MS_PER_MINUTE;
  const shifted = zonedWallClockToInstant(
    `${parts.year}-${padUnit(parts.month)}-${padUnit(parts.day)}`,
    `${padUnit(parts.hour)}:${padUnit(parts.minute)}`,
    days,
    timeZone,
  );

  return new Date(shifted.getTime() + subMinuteMs);
};
